/**
 * Tax ID validation constants and patterns
 * Based on design system patterns with FF-4166 requirements for US entities
 */
import { isUSA } from "../../../../common/utils/is_usa";
import { getNameFromCountry } from "../../../../utils/country_utils";

// Error messages
export const TAX_ID_VALIDATION_ERRORS = {
  INVALID_FORMAT: "Tax ID must be in EIN format (XX-XXXXXXX) for business entities",
  SSN_NOT_ALLOWED:
    "SSN format is not allowed. Please use EIN format (XX-XXXXXXX) for business entities",
  ITIN_NOT_ALLOWED:
    "ITIN format is not allowed. Please use EIN format (XX-XXXXXXX) for business entities",
  UNFORMATTED_EIN: "Please format your EIN with a dash (XX-XXXXXXX). Example: 12-3456789",
  REQUIRED_WHEN_COUNTRY_PROVIDED: "Tax ID number is required when country is selected",
  COUNTRY_REQUIRED: "Country is required when Tax ID is provided",
} as const;

// Design system tax ID patterns - exact copy from design system
const USITINFormat = /^\s*\d{3}-\d{2}-\d{4}\s*$/;
const USEntitySSNFormats = /^\s*(?:\d{3}-\d{2}-\d{4}|\d{2}-\d{7})\s*$/;
const USEntityFormat = /^\s*\d{2}-\d{7}\s*$/;
const CAITINFormat = /^\s*(\d{9})\s*$/;
const CAEntityFormat = /^\s*\d{9}\s[A-Z]{2}\d{4}\s*$/;
const CAEntityIndividualFormat = /^\s*(?:\d{9}\s[A-Z]{2}\d{4}|\d{9})\s*$/;
const UKGermanyIndividualEntityFormat = /^\s*.{8,}\s*$/;
const IndiaIndividualEntityFormat = /^\s*(?:.{10}$|^.{15})\s*$/;
const SingaporeIndividualFormat = /^\s*.{9}\s*$/;
const SingaporeEntityFormat = /^\s*(?:.{9}$|^.{10})\s*$/;
const IsraelIndividualEntityFormat = /^\s*.{9}\s*$/;
const MXJPCHIndividualEntityFormat = /^\s*.{12,}\s*$/;
const CNIndividualEntityFormat = /^\s*.{18}\s*$/;
const UAEIndividualEntityFormat = /^\s*.{15,}\s*$/;
const SaudiArabiaIndividualEntityFormat = /^\s*.{10,}\s*$/;
const BelgiumIndividualFormat = /^\s*.{11,}\s*$/;
const BelgiumEntityFormat = /^\s*.{10,}\s*$/;

const USFormatMessageITIN = "Format must be ###-##-####";
const USFormatMessageEntity = "Format must be ##-#######";
const USFormatMessageEntitySSN = "Format must be ###-##-#### or ##-#######";
const CAFormatMessageITIN = "Format must be #########";
const CAFormatMessageEntity = "Format must be ######### XX####";
const CAFormatMessageEntityIndividual = "Format must be ######### or ######### XX####";
const UKGermanyFormatMessageIndividualEntity = "Enter at least 8 characters";
const IndiaFormatMessageIndividualEntity = "Enter any 10 or 15 characters";
const SingaporeFormatMessageIndividual = "Enter any 9 characters";
const SingaporeFormatMessageEntity = "Enter any 9 or 10 characters";
const IsraelFormatMessageIndividualEntity = "Enter any 9 characters";
const MXJPCHFormatMessageIndividualEntity = "Enter at least 12 characters";
const CNFormatMessageIndividualEntity = "Enter any 18 characters";
const UAEFormatMessageIndividualEntity = "Enter at least 15 characters";
const SaudiArabiaFormatMessageIndividualEntity = "Enter at least 10 characters";
const BelgiumFormatMessageIndividual = "Enter at least 11 characters";
const BelgiumFormatMessageEntity = "Enter at least 10 characters";

/**
 * Design system-compatible tax ID validation
 * @param taxId - The tax ID to validate
 * @param countryValue - The selected country value
 * @param accountType - Account type (0=Individual, 1=Entity, 2=EntityIndividual)
 * @returns validation result object
 */
// Design system pattern maps
const taxIdPatterns = new Map<string, RegExp[]>();
taxIdPatterns.set("US", [USITINFormat, USEntityFormat, USEntitySSNFormats]);
taxIdPatterns.set("CA", [CAITINFormat, CAEntityFormat, CAEntityIndividualFormat]);
taxIdPatterns.set("GB", [
  UKGermanyIndividualEntityFormat,
  UKGermanyIndividualEntityFormat,
  UKGermanyIndividualEntityFormat,
]);
taxIdPatterns.set("IN", [
  IndiaIndividualEntityFormat,
  IndiaIndividualEntityFormat,
  IndiaIndividualEntityFormat,
]);
taxIdPatterns.set("DE", [
  UKGermanyIndividualEntityFormat,
  UKGermanyIndividualEntityFormat,
  UKGermanyIndividualEntityFormat,
]);
taxIdPatterns.set("SG", [
  SingaporeIndividualFormat,
  SingaporeEntityFormat,
  SingaporeEntityFormat,
]);
taxIdPatterns.set("IL", [
  IsraelIndividualEntityFormat,
  IsraelIndividualEntityFormat,
  IsraelIndividualEntityFormat,
]);
taxIdPatterns.set("MX", [
  MXJPCHIndividualEntityFormat,
  MXJPCHIndividualEntityFormat,
  MXJPCHIndividualEntityFormat,
]);
taxIdPatterns.set("JP", [
  MXJPCHIndividualEntityFormat,
  MXJPCHIndividualEntityFormat,
  MXJPCHIndividualEntityFormat,
]);
taxIdPatterns.set("CH", [
  MXJPCHIndividualEntityFormat,
  MXJPCHIndividualEntityFormat,
  MXJPCHIndividualEntityFormat,
]);
taxIdPatterns.set("CN", [
  CNIndividualEntityFormat,
  CNIndividualEntityFormat,
  CNIndividualEntityFormat,
]);
taxIdPatterns.set("AE", [
  UAEIndividualEntityFormat,
  UAEIndividualEntityFormat,
  UAEIndividualEntityFormat,
]);
taxIdPatterns.set("SA", [
  SaudiArabiaIndividualEntityFormat,
  SaudiArabiaIndividualEntityFormat,
  SaudiArabiaIndividualEntityFormat,
]);
taxIdPatterns.set("BE", [BelgiumIndividualFormat, BelgiumEntityFormat, BelgiumEntityFormat]);

const taxFormatMessagePatterns = new Map<string, string[]>();
taxFormatMessagePatterns.set("US", [
  USFormatMessageITIN,
  USFormatMessageEntity,
  USFormatMessageEntitySSN,
]);
taxFormatMessagePatterns.set("CA", [
  CAFormatMessageITIN,
  CAFormatMessageEntity,
  CAFormatMessageEntityIndividual,
]);
taxFormatMessagePatterns.set("GB", [
  UKGermanyFormatMessageIndividualEntity,
  UKGermanyFormatMessageIndividualEntity,
  UKGermanyFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("IN", [
  IndiaFormatMessageIndividualEntity,
  IndiaFormatMessageIndividualEntity,
  IndiaFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("DE", [
  UKGermanyFormatMessageIndividualEntity,
  UKGermanyFormatMessageIndividualEntity,
  UKGermanyFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("SG", [
  SingaporeFormatMessageIndividual,
  SingaporeFormatMessageEntity,
  SingaporeFormatMessageEntity,
]);
taxFormatMessagePatterns.set("IL", [
  IsraelFormatMessageIndividualEntity,
  IsraelFormatMessageIndividualEntity,
  IsraelFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("MX", [
  MXJPCHFormatMessageIndividualEntity,
  MXJPCHFormatMessageIndividualEntity,
  MXJPCHFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("JP", [
  MXJPCHFormatMessageIndividualEntity,
  MXJPCHFormatMessageIndividualEntity,
  MXJPCHFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("CH", [
  MXJPCHFormatMessageIndividualEntity,
  MXJPCHFormatMessageIndividualEntity,
  MXJPCHFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("CN", [
  CNFormatMessageIndividualEntity,
  CNFormatMessageIndividualEntity,
  CNFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("AE", [
  UAEFormatMessageIndividualEntity,
  UAEFormatMessageIndividualEntity,
  UAEFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("SA", [
  SaudiArabiaFormatMessageIndividualEntity,
  SaudiArabiaFormatMessageIndividualEntity,
  SaudiArabiaFormatMessageIndividualEntity,
]);
taxFormatMessagePatterns.set("BE", [
  BelgiumFormatMessageIndividual,
  BelgiumFormatMessageEntity,
  BelgiumFormatMessageEntity,
]);

/**
 * Extract country code from country value (handles both formats)
 * @param countryValue - Country value (could be "US" or "United States:Q291bnRyeU9iamVjdFR5cGU6MjM0")
 * @returns 2-letter country code or null
 */
const getCountryCode = (countryValue: string): string | null => {
  if (!countryValue) return null;

  // If it's already a 2-letter code, return it
  if (countryValue.length === 2) {
    return countryValue.toUpperCase();
  }

  // Extract country name and convert to code
  const countryName = getNameFromCountry(countryValue);
  if (isUSA(countryName)) {
    return "US";
  }

  // Add more country mappings as needed
  // For now, we'll try to extract from the country value format
  if (countryValue.includes("Canada")) return "CA";
  if (countryValue.includes("United Kingdom") || countryValue.includes("Britain")) return "GB";
  if (countryValue.includes("Germany")) return "DE";
  if (countryValue.includes("India")) return "IN";
  if (countryValue.includes("Singapore")) return "SG";
  if (countryValue.includes("Israel")) return "IL";
  if (countryValue.includes("Mexico")) return "MX";
  if (countryValue.includes("Japan")) return "JP";
  if (countryValue.includes("Switzerland")) return "CH";
  if (countryValue.includes("China")) return "CN";
  if (countryValue.includes("United Arab Emirates") || countryValue.includes("UAE"))
    return "AE";
  if (countryValue.includes("Saudi Arabia")) return "SA";
  if (countryValue.includes("Belgium")) return "BE";

  return null;
};

export const validateTaxId = (
  taxId: string,
  countryValue?: string,
  accountType: number = 1, // Default to Entity
) => {
  if (!taxId || !taxId.trim()) {
    return { isValid: true, error: null };
  }

  if (!countryValue || countryValue.length === 0) {
    return { isValid: true, error: null };
  }

  const countryCode = getCountryCode(countryValue);
  if (!countryCode) {
    return { isValid: true, error: null }; // No validation for unsupported countries
  }

  // Check if we have patterns for this country
  if (taxIdPatterns.has(countryCode)) {
    const patterns = taxIdPatterns.get(countryCode);
    if (patterns && !patterns[accountType]?.test(taxId)) {
      const formatMessages = taxFormatMessagePatterns.get(countryCode);
      const errorMessage = formatMessages?.[accountType] || "Invalid tax ID format";
      return {
        isValid: false,
        error: errorMessage,
      };
    }
  }

  return { isValid: true, error: null };
};

/**
 * Yup validation test function for tax ID fields
 */
export const yupTaxIdValidationTest = function (
  this: import("yup").TestContext,
  taxIdValue: string,
  taxCountryFieldName: string = "taxCountryId",
  accountType: number = 1, // Default to Entity
): boolean | import("yup").ValidationError {
  if (!taxIdValue || !taxIdValue.trim()) {
    return true;
  }

  const parentValues = this.parent as Record<string, unknown>;
  const countryValue = (parentValues[taxCountryFieldName] as string) || "";

  if (!countryValue) {
    return true;
  }

  const validation = validateTaxId(taxIdValue, countryValue, accountType);

  if (!validation.isValid && validation.error) {
    return this.createError({ message: validation.error });
  }

  return true;
};
